import { PencilIcon } from "@heroicons/react/24/outline";
import { WorkflowId } from "@sp-crm/core";
import { workflowPayloadFromWorkflow } from "components/admin/tenant/workflow/workflow-edit";
import { useAdvancedSearchQuery } from "components/advanced-search/advanced-search-request";
import { SupportEmailLink } from "components/app/support-email-link";
import { ContentFull, ContentHeader } from "components/layout";
import { Badge } from "components/ui/badge";
import { BlankSlate } from "components/ui/blank-slate";
import { Breadcrumbs } from "components/ui/breadcrumbs";
import { InlineBanner } from "components/ui/inline-banner";
import { defaultLinkStyle } from "components/ui/link";
import { ListSection } from "components/ui/list-section";
import { SecondaryButton, SecondaryCompactButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import { StatTile } from "components/ui/stat-tile";
import { WorkflowRunRow } from "components/workflow/workflow-entity-executions";
import { executionActionLabel } from "components/workflow/workflow-execution-helper";
import {
    AdvancedSearchRequest,
    BridgeEntityResult,
    GetWorkflowSettingsResult,
    useCancelWorkflowExecutionMutation,
} from "generated/graphql";
import React from "react";
import { useQueryClient } from "react-query";
import { Link } from "react-router-dom";
import { useProductName } from "store/selectors/branding";
import { useBridgeTypes } from "store/selectors/bridge";
import { handleEvent } from "util/user-events";
import { EditableWorkflow } from "../types";
import { workflowEventProperties } from "./builder-analytics";
import { useWorkflowEditor } from "./use-workflow-editor";
import { detectBlueprint } from "./workflow-blueprint";
import {
    recentWorkflowRunsRequest,
    upcomingWorkflowRunsRequest,
    withMoreWorkflowRuns,
    WorkflowRunCountKind,
    workflowRunCountKinds,
    workflowRunCountRequest,
    workflowRunCountTitle,
    WorkflowRunListItem,
    workflowRunsFromSearch,
} from "./workflow-details-helper";
import {
    useWorkflowStatusActions,
    WorkflowStatusActions,
} from "./workflow-status-actions";
import { WorkflowSummaryCard } from "./workflow-summary-card";

interface RunCountProps {
    workflowId: WorkflowId;
    kind: WorkflowRunCountKind;
    entityMetadataList: BridgeEntityResult[];
}

const RunCount: React.FC<RunCountProps> = props => {
    const { workflowId, kind, entityMetadataList } = props;
    const request = React.useMemo(
        () => workflowRunCountRequest(workflowId, kind),
        [workflowId, kind],
    );
    const query = useAdvancedSearchQuery(request, entityMetadataList);
    const total = query.data?.advancedSearch.total;

    return (
        <StatTile
            label={workflowRunCountTitle(kind)}
            value={total === undefined ? "–" : total.toLocaleString()}
        />
    );
};

interface RunTitleProps {
    run: WorkflowRunListItem;
    workflow: EditableWorkflow;
}

const RunTitle: React.FC<RunTitleProps> = props => {
    const { run, workflow } = props;
    const actionLabel = executionActionLabel({ actionId: run.actionId, workflow });

    return (
        <>
            <Link to={run.appLink} className={defaultLinkStyle}>
                {run.name}
            </Link>
            {actionLabel ? (
                <span className="text-sm text-gray-500"> &middot; {actionLabel}</span>
            ) : null}
        </>
    );
};

interface WorkflowRunListProps {
    label: string;
    emptyMessage: string;
    request: AdvancedSearchRequest;
    onShowMore: () => void;
    workflow: EditableWorkflow;
    entityMetadataList: BridgeEntityResult[];
    onCancelRun: (run: WorkflowRunListItem) => Promise<void>;
}

const WorkflowRunList: React.FC<WorkflowRunListProps> = props => {
    const {
        label,
        emptyMessage,
        request,
        onShowMore,
        workflow,
        entityMetadataList,
        onCancelRun,
    } = props;
    const query = useAdvancedSearchQuery(request, entityMetadataList);
    const results = query.data?.advancedSearch ?? null;
    const runs = React.useMemo(
        () => workflowRunsFromSearch(results?.entities ?? [], workflow.attachTo),
        [results?.entities, workflow.attachTo],
    );
    const total = results?.total ?? 0;

    return (
        <ListSection
            label={label}
            count={total}
            tone="accent"
            disclosure={{ kind: "static" }}>
            {!results ? (
                <div className="p-4">
                    <Spinner />
                </div>
            ) : runs.length === 0 ? (
                <div className="p-4">
                    <BlankSlate>{emptyMessage}</BlankSlate>
                </div>
            ) : (
                <div className="divide-y divide-gray-100">
                    {runs.map(run => (
                        <div key={run.id} className="px-4 py-2.5">
                            <WorkflowRunRow
                                title={<RunTitle run={run} workflow={workflow} />}
                                chip={null}
                                run={{
                                    scheduledAt: run.scheduledAt,
                                    cancelledAt: null,
                                    finishedAt: run.finishedAt,
                                    result: run.result,
                                    resultDetail: run.resultDetail,
                                }}
                                onCancel={() => onCancelRun(run)}
                            />
                        </div>
                    ))}
                </div>
            )}
            {results && total > runs.length ? (
                <div className="flex items-center justify-between border-t border-gray-100 px-4 py-2 text-xs text-gray-500">
                    <span>
                        Showing {runs.length} of {total}
                    </span>
                    <SecondaryCompactButton onClick={onShowMore}>
                        Show more
                    </SecondaryCompactButton>
                </div>
            ) : null}
        </ListSection>
    );
};

interface WorkflowDetailsPageProps {
    workflow: EditableWorkflow;
    workflowSettings: GetWorkflowSettingsResult;
    refetch: () => Promise<void>;
}

export const WorkflowDetailsPage: React.FC<WorkflowDetailsPageProps> = props => {
    const { workflow, workflowSettings, refetch } = props;
    const productName = useProductName();
    const queryClient = useQueryClient();
    const { entityTypes: entityMetadataList } = useBridgeTypes();
    const editor = useWorkflowEditor(workflow, refetch);
    const cancelMutation = useCancelWorkflowExecutionMutation();

    const eventProperties = React.useMemo(
        () =>
            workflowEventProperties(
                workflow,
                detectBlueprint(workflowPayloadFromWorkflow(workflow)),
            ),
        [workflow],
    );
    const actions = useWorkflowStatusActions({
        workflow,
        editor,
        eventProperties,
        refetch,
    });

    const viewReported = React.useRef(false);
    React.useEffect(() => {
        if (viewReported.current) {
            return;
        }
        viewReported.current = true;
        handleEvent("workflow-builder-detail-view", eventProperties);
    }, [eventProperties]);

    const [upcomingRequest, setUpcomingRequest] = React.useState(() =>
        upcomingWorkflowRunsRequest({
            workflowId: workflow.id,
            attachTo: workflow.attachTo,
        }),
    );
    const [recentRequest, setRecentRequest] = React.useState(() =>
        recentWorkflowRunsRequest({
            workflowId: workflow.id,
            attachTo: workflow.attachTo,
        }),
    );
    const showMoreUpcoming = React.useCallback(
        () => setUpcomingRequest(withMoreWorkflowRuns),
        [],
    );
    const showMoreRecent = React.useCallback(
        () => setRecentRequest(withMoreWorkflowRuns),
        [],
    );

    const cancelRun = React.useCallback(
        async (run: WorkflowRunListItem) => {
            await cancelMutation.mutateAsync({ workflowExecutionId: run.id });
            handleEvent("workflow-builder-run-cancel", eventProperties);
            await queryClient.invalidateQueries("advancedSearch");
        },
        [cancelMutation, eventProperties, queryClient],
    );

    return (
        <ContentFull>
            <div className="space-y-4">
                <div>
                    <Breadcrumbs
                        links={[{ label: "All workflows", to: "/settings/workflow" }]}
                        current={workflow.name}
                    />
                    <div className="flex justify-between items-center gap-4 flex-wrap mt-1">
                        <ContentHeader>
                            <span className="inline-flex items-center gap-2">
                                {workflow.name}
                                {actions.isDraft ? (
                                    <Badge value="Draft" type="info" />
                                ) : (
                                    <Badge value="Active" type="success" />
                                )}
                            </span>
                        </ContentHeader>
                        <div className="flex items-center gap-2">
                            <Link to={`/settings/workflow/${workflow.id}/edit`}>
                                <SecondaryButton>
                                    <div className="flex items-center space-x-2">
                                        <PencilIcon className="w-5 h-5" />
                                        <span>Edit</span>
                                    </div>
                                </SecondaryButton>
                            </Link>
                            <WorkflowStatusActions actions={actions} />
                        </div>
                    </div>
                </div>
                {!actions.editable ? (
                    <InlineBanner type="info">
                        This workflow requires administrative access to additional{" "}
                        {productName} regions to make changes. Contact your agency
                        administrator or <SupportEmailLink /> to request changes.
                    </InlineBanner>
                ) : null}
                {actions.isDraft ? (
                    <InlineBanner type="info">
                        This workflow is a draft and will not run until you publish it.
                    </InlineBanner>
                ) : null}
                <WorkflowSummaryCard
                    workflow={workflow}
                    workflowSettings={workflowSettings}
                />
                <dl className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {workflowRunCountKinds.map(kind => (
                        <RunCount
                            key={kind}
                            workflowId={workflow.id}
                            kind={kind}
                            entityMetadataList={entityMetadataList}
                        />
                    ))}
                </dl>
                <WorkflowRunList
                    label="Upcoming runs"
                    emptyMessage="No runs are scheduled."
                    request={upcomingRequest}
                    onShowMore={showMoreUpcoming}
                    workflow={workflow}
                    entityMetadataList={entityMetadataList}
                    onCancelRun={cancelRun}
                />
                <WorkflowRunList
                    label="Recent runs"
                    emptyMessage="This workflow hasn't run yet."
                    request={recentRequest}
                    onShowMore={showMoreRecent}
                    workflow={workflow}
                    entityMetadataList={entityMetadataList}
                    onCancelRun={cancelRun}
                />
            </div>
        </ContentFull>
    );
};
